/**
 * GenerationTypesPanel — left rail of the Generation shell (Block 2026-08-02).
 *
 * Spec ref: PRODUCT_SPEC_WORKFLOW_MATCHING_TRANSLATION_GENERATION_V1.md §16.2–16.4
 * (left panel = scrollable list of available generation types, grouped).
 *
 * Lists Text / SEO / Custom content types. Selecting a type opens its settings
 * in the right panel (GenerationSettingsPanel). Each item shows:
 *   - the type label;
 *   - an enabled indicator (from contentConfigs[key].enabled);
 *   - an honest "runtime: soon" badge for types whose BE module is not
 *     registered yet (only `product_description` runs today).
 */
import { useTranslation } from 'react-i18next';
import { Clock } from 'lucide-react';

import { useGenerationStore } from '@/store/generationStore';
import {
  CONTENT_TYPES,
  CONTENT_GROUPS,
  type ContentGroup,
} from '@/components/generation/contentTypes';
import { cn } from '@/lib/utils';

export function GenerationTypesPanel() {
  const { t } = useTranslation();
  const selectedContentType = useGenerationStore((s) => s.selectedContentType);
  const setSelectedContentType = useGenerationStore((s) => s.setSelectedContentType);
  const contentConfigs = useGenerationStore((s) => s.contentConfigs);

  return (
    <div className="card flex flex-col min-h-[320px]">
      <div className="text-sm font-semibold mb-3">
        {t('generation.types.title', { defaultValue: 'Content types' })}
      </div>
      <div className="space-y-4 overflow-y-auto">
        {CONTENT_GROUPS.map((group) => {
          const types = CONTENT_TYPES.filter((ty) => ty.group === (group.key as ContentGroup));
          if (types.length === 0) return null;
          return (
            <div key={group.key}>
              <div className="text-[11px] uppercase tracking-wider text-surface-500 mb-1.5">
                {t(group.labelKey, { defaultValue: group.defaultLabel })}
              </div>
              <ul className="space-y-1">
                {types.map((ty) => {
                  const isSel = selectedContentType === ty.key;
                  const cfg = contentConfigs[ty.key];
                  const isEnabled = cfg?.enabled ?? ty.defaultConfig.enabled;
                  return (
                    <li key={ty.key}>
                      <button
                        onClick={() => setSelectedContentType(ty.key)}
                        className={cn(
                          'w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-colors',
                          isSel
                            ? 'bg-primary-500/15 border-primary-500/30 text-primary-200'
                            : 'bg-surface-800/30 border-transparent text-surface-300 hover:border-surface-700',
                        )}
                      >
                        <span
                          className={cn(
                            'w-2 h-2 rounded-full shrink-0',
                            isEnabled ? 'bg-success-tint' : 'bg-surface-600',
                          )}
                          title={
                            isEnabled
                              ? t('generation.types.enabled', { defaultValue: 'Enabled' })
                              : t('generation.types.disabled', { defaultValue: 'Disabled' })
                          }
                        />
                        <span className="flex-1 truncate">
                          {t(ty.labelKey, { defaultValue: ty.defaultLabel })}
                        </span>
                        {!ty.runtimeEnabled && (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-surface-700/50 text-surface-400 shrink-0"
                            title={t('generation.types.runtimeSoonHint', {
                              defaultValue:
                                'Settings are saved; runtime execution is coming soon.',
                            })}
                          >
                            <Clock className="w-3 h-3" />
                            {t('generation.types.runtimeSoon', { defaultValue: 'soon' })}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}